import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../useAuth";
import axios from "axios";
import { server } from "../server";
import {
  FaBell,
  FaSignOutAlt,
  FaCog,
  FaChevronDown,
  FaUser,
  FaSun,
  FaMoon,
  FaCalendarAlt,
  FaSearch,
} from "react-icons/fa";
import { MdMenu, MdClose } from "react-icons/md";

export default function Header({ isMobileNavOpen, onToggleMobileNav }) {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [profileData, setProfileData] = useState(null);

  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) return;

      try {
        const response = await axios.get(`${server}/auth/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          setProfileData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchProfile();
  }, [token]);

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const displayName = profileData?.fullName || user?.username || "User";
  const roleName = profileData?.role?.name || user?.role?.name || "User";
  const profileImage = profileData?.profilePictureUrl;

  return (
    <header className="bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl border-b border-slate-200/80 dark:border-slate-800/80 h-16 flex items-center justify-between px-3 sm:px-6 sticky top-0 z-40 shadow-xs transition-colors duration-300">
      {/* Left Section */}
      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        <button
          onClick={onToggleMobileNav}
          className="lg:hidden p-2 text-slate-500 dark:text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors shrink-0"
          aria-label="Toggle Navigation Menu"
        >
          {isMobileNavOpen ? <MdClose size={22} /> : <MdMenu size={22} />}
        </button>

        <div
          onClick={() => navigate("/my")}
          className="flex items-center gap-3 min-w-0 cursor-pointer group"
          title="Go to Dashboard"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xs sm:text-base font-bold text-blue-700 dark:text-white tracking-tight truncate max-w-[140px] sm:max-w-none group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors">
                School Management Portal
              </h1>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-[10px] sm:text-[11px] font-semibold text-blue-500 dark:text-slate-400 flex items-center gap-1.5 truncate">
                <FaCalendarAlt className="text-blue-700 text-[10px]" />
                {new Date().toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 sm:p-2.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors border border-slate-200/80 dark:border-slate-800"
          title={
            theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"
          }
        >
          {theme === "light" ? (
            <FaMoon size={14} className="text-slate-700 sm:w-4 sm:h-4" />
          ) : (
            <FaSun size={14} className="text-amber-400 sm:w-4 sm:h-4" />
          )}
        </button>

        {/* Profile Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center gap-2 sm:gap-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl p-1 sm:p-1.5 sm:pr-3 transition-colors border border-slate-200/80 dark:border-slate-800 shadow-2xs"
          >
            <div className="relative">
              {profileImage ? (
                <img
                  className="w-8 h-8 rounded-xl border border-slate-200 dark:border-slate-700 object-cover"
                  src={profileImage}
                  alt="profile"
                />
              ) : (
                <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-sky-500 to-blue-600 flex items-center justify-center text-white font-black text-xs shadow-xs">
                  {getInitials(displayName)}
                </div>
              )}
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white dark:border-slate-900" />
            </div>

            <div className="text-left hidden md:block">
              <p className="text-xs font-black text-slate-900 dark:text-white leading-tight">
                {displayName}
              </p>
              <p className="text-[10px] font-bold text-sky-600 dark:text-sky-400">
                {roleName}
              </p>
            </div>

            <FaChevronDown
              className={`text-slate-400 dark:text-slate-500 text-[10px] hidden md:block transition-transform duration-200 ${
                showProfileMenu ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          {showProfileMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowProfileMenu(false)}
              ></div>
              <div className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-1.5rem)] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-800 z-20 overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-sky-500 to-blue-600 text-white">
                  <div className="flex items-center gap-3">
                    {profileImage ? (
                      <img
                        className="w-11 h-11 rounded-xl border-2 border-white object-cover"
                        src={profileImage}
                        alt="profile"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center text-white font-black">
                        {getInitials(displayName)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-sm truncate">
                        {displayName}
                      </p>
                      <p className="text-[11px] text-sky-100 truncate">
                        {profileData?.email || "No email"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 inline-flex items-center px-2.5 py-0.5 bg-white/20 rounded-full text-[10px] font-black uppercase tracking-wider">
                    {roleName}
                  </div>
                </div>

                <div className="py-2">
                  <button
                    onClick={() => {
                      navigate("/profile");
                      setShowProfileMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors"
                  >
                    <FaUser className="text-gray-400 dark:text-slate-500" />
                    <span>My Profile</span>
                  </button>

                  <button
                    onClick={() => {
                      navigate("/settings");
                      setShowProfileMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors"
                  >
                    <FaCog className="text-gray-400 dark:text-slate-500" />
                    <span>System Settings</span>
                  </button>
                </div>

                <div className="border-t border-gray-100 dark:border-slate-800 py-2">
                  <button
                    onClick={() => {
                      logout();
                      setShowProfileMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 flex items-center gap-3 transition-colors"
                  >
                    <FaSignOutAlt />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
